import { get as emberGet } from '@ember/object';
import { isEmpty } from '@ember/utils';

// naturalSort returns a comparator that sorts objects by the given sortProp.
// Strings are compared with locale-aware case-insensitive `localeCompare`;
// empty values always sink below non-empty values regardless of sortDir.
//
// Options:
//   get     — when true, read field values via Ember's `get()` so dotted
//             paths ('attendance.term1.present') are resolved. Defaults to
//             plain bracket access.
//   numeric — predicate `(sortProp) => boolean`. When it returns true,
//             values are compared with `>`/`<` instead of `localeCompare`.
//             Used by callers that mix string and numeric columns behind
//             the same sortProp (e.g. attendance registers).
interface NaturalSortOptions {
  get?: boolean;
  numeric?: (sortProp: string) => boolean;
}

export function naturalSort<T extends object>(
  sortProp: string,
  sortDir?: string,
  options: NaturalSortOptions = {},
): (a: T, b: T) => number {
  const readField = options.get
    ? (obj: T): unknown => emberGet(obj, sortProp)
    : (obj: T): unknown => (obj as Record<string, unknown>)[sortProp];
  const isNumeric = options.numeric || (() => false);

  return function (a: T, b: T) {
    let result = 0;
    if (isEmpty(sortProp)) {
      return result;
    }
    const aField = readField(a);
    const bField = readField(b);

    // Non-empty always trumps empty, no matter which flavour of empty (null, undefined, empty string, etc)
    if (isEmpty(aField) && !isEmpty(bField)) {
      result = -1;
    } else if (isEmpty(bField) && !isEmpty(aField)) {
      result = 1;
    } else if (isEmpty(bField) && isEmpty(aField)) {
      result = 0;
    } else if (isNumeric(sortProp)) {
      // Coerce with Number() so numeric strings ("10" vs "2") sort correctly.
      // Follows the same pattern as `utils/filtering.js`'s numeric filters.
      const aNum = Number(aField);
      const bNum = Number(bField);
      if (aNum > bNum) {
        result = 1;
      } else if (aNum < bNum) {
        result = -1;
      }
    } else {
      // Non-numeric columns hold strings.
      const aString = (aField as string).toLowerCase().trim();
      const bString = (bField as string).toLowerCase().trim();
      result = aString.localeCompare(bString, undefined, {
        numeric: true,
        caseFirst: 'false',
        sensitivity: 'base',
        ignorePunctuation: false,
      });
    }
    if (sortDir === 'desc') {
      result = -1 * result;
    }
    return result;
  };
}

// Sequenced is a record with a 1-based sequence that a user can reorder (for
// example goal subjects, pages and tags). 0, the Go zero value, means that
// the record has no sequence.
export interface Sequenced {
  sequence: number;
  label?: string;
}

// bySequence sorts records by sequence, then by label. Records with no
// sequence (0) go last. Use it only for models with a 1-based sequence:
// assessments and report sections are 0-based, so 0 is a real position there.
export function bySequence(a: Sequenced, b: Sequenced): number {
  const aSequence = a.sequence || Infinity;
  const bSequence = b.sequence || Infinity;
  if (aSequence !== bSequence) {
    return aSequence < bSequence ? -1 : 1;
  }
  return (a.label ?? '').localeCompare(b.label ?? '');
}
